import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useCreateExpenseRecordMutation,
  useExpenseRecordQuery,
  useSubmitExpenseRecordMutation,
  useUpdateExpenseRecordMutation,
} from "../api/queries";
import { ExpenseRecordForm } from "../ui/ExpenseRecordForm";

type ExpenseRecordFormPageProps = {
  mode: "create" | "edit";
};

export function ExpenseRecordFormPage({ mode }: ExpenseRecordFormPageProps) {
  const params = useParams({ strict: false }) as { id?: string };
  const id = mode === "edit" ? params.id : undefined;
  const nav = useNavigate();
  const isEdit = mode === "edit" && !!id;

  const { data: record, isLoading } = useExpenseRecordQuery(
    isEdit && id ? id : null,
  );
  const createMutation = useCreateExpenseRecordMutation();
  const updateMutation = useUpdateExpenseRecordMutation(id ?? "");
  const submitMutation = useSubmitExpenseRecordMutation(id ?? "");

  const breadcrumb = (
    <Breadcrumb
      items={[
        { label: "ແຜງຄວບຄຸມ", to: "/app/expenses/overview" },
        { label: "ບັນທຶກຄ່າໃຊ້ຈ່າຍ", to: "/app/expenses" },
        { label: isEdit ? "ແກ້ໄຂ" : "ສ້າງໃໝ່" },
      ]}
    />
  );

  if (isEdit && isLoading && !record) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        </Main>
      </>
    );
  }

  if (isEdit && record && !["draft", "rejected"].includes(record.status)) {
    return (
      <>
        <Header>{breadcrumb}</Header>
        <Main>
          <p className="text-muted-foreground">
            ບໍ່ສາມາດແກ້ໄຂລາຍການນີ້ໄດ້ (ສະຖານະ: {record.status})
          </p>
          <Button
            className="mt-4"
            variant="outline"
            onClick={() => {
              if (!id) return;
              nav({ to: "/app/expenses/$id", params: { id } });
            }}
          >
            ກັບໄປລາຍລະອຽດ
          </Button>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>{breadcrumb}</Header>
      <Main>
        <div className="mx-auto flex max-w-3xl flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-semibold text-2xl tracking-tight">
                {isEdit ? "ແກ້ໄຂຄ່າໃຊ້ຈ່າຍ" : "ບັນທຶກຄ່າໃຊ້ຈ່າຍໃໝ່"}
              </h1>
              <p className="mt-1 text-muted-foreground text-sm">
                {isEdit && record
                  ? record.number
                  : "ກະກຽມຂໍ້ມູນ ແລະ ສົ່ງອະນຸມັດເມື່ອພ້ອມ"}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                nav({
                  to: isEdit ? "/app/expenses/$id" : "/app/expenses",
                  params: isEdit && id ? { id } : undefined,
                })
              }
            >
              <ArrowLeftIcon className="mr-2 size-4" />
              ກັບຄືນ
            </Button>
          </div>

          <Card className="gap-0 overflow-hidden py-0">
            <CardHeader className="space-y-1 border-b px-6 py-4">
              <CardTitle className="text-base">ຂໍ້ມູນຄ່າໃຊ້ຈ່າຍ</CardTitle>
              <CardDescription>
                {isEdit
                  ? "ແກ້ໄຂໄດ້ເມື່ອສະຖານະເປັນຮ່າງ ຫຼື ຖືກປະຕິເສດ"
                  : "ບັນທຶກຮ່າງກ່ອນ ແລ້ວສົ່ງອະນຸມັດເມື່ອພ້ອມ"}
              </CardDescription>
            </CardHeader>

            <ExpenseRecordForm
              mode={mode}
              submitting={
                createMutation.isPending ||
                updateMutation.isPending ||
                submitMutation.isPending
              }
              initialValues={
                isEdit && record
                  ? {
                      expenseDate: record.expenseDate.slice(0, 10),
                      categoryId: record.categoryId,
                      payeeName: record.payeeName,
                      amount: record.amount,
                      description: record.description ?? "",
                      notes: record.notes ?? "",
                      performanceEventId: record.performanceEventId ?? "",
                    }
                  : undefined
              }
              onSubmit={async (values, submit) => {
                try {
                  const payload = {
                    expenseDate: values.expenseDate,
                    categoryId: values.categoryId,
                    payeeName: values.payeeName,
                    amount: Number(values.amount),
                    description: values.description || undefined,
                    notes: values.notes || undefined,
                    performanceEventId: values.performanceEventId || undefined,
                  };

                  if (isEdit) {
                    await updateMutation.mutateAsync(payload);
                    if (submit) {
                      await submitMutation.mutateAsync();
                    }
                    if (id) {
                      nav({ to: "/app/expenses/$id", params: { id } });
                    }
                    return;
                  }

                  const result = await createMutation.mutateAsync({
                    ...payload,
                    submit,
                  });
                  nav({
                    to: "/app/expenses/$id",
                    params: { id: result.id },
                  });
                } catch {
                  toast.error("ບັນທຶກລົ້ມເຫຼວ");
                }
              }}
            />
          </Card>
        </div>
      </Main>
    </>
  );
}
